//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import angular, { IOnChangesObject } from 'angular';

import { IShareCreatorClient } from '@share/models/shareStateModel';
import { getStreamsDrawerLayout } from '@streams/streamsUtils';
import { IStream } from '../model/IStream';
import {
  StreamsDrawerStreamType,
  StreamsLaunchContext,
} from '../streams-drawer-constants';
import template from './streams-drawer.html';

// Import the react equivalent
import '@streams-drawer/streams-drawer-sidebar/StreamsDrawerSidebarReact';

import '@streams/angular/streams-creation-modal';
import '@streams/components/StreamDeleteConfirmationModal';
import '@streams/angular/streams-drawer-asset-feeds-close-confirmation-modal';
import '@streams/angular/streams-drawer-asset-feeds-setup';
import '@streams/angular/streams-drawer-asset-feeds-setup-modal';
import '@streams/angular/streams-drawer-asset-feeds-setup-modal';
import '@streams/angular/auto-approval-modal';
import '@streams/angular/auto-approval-entry-modal';
import '@streams/angular/auto-active-config-rules';
import '../streams-drawer-action-bar/streams-drawer-action-bar';
import '../streams-drawer-content/streams-drawer-content';
import '../streams-drawer-deprecation-banner/streams-drawer-deprecation-banner';
import '../streams-drawer-header/streams-drawer-header';
import '../streams-drawer-recommended-header/streams-drawer-recommended-header';
import '../streams-drawer-sidebar/streams-drawer-sidebar';
import '../streams-drawer-standard-setup/streams-drawer-standard-setup-container';
import '../streams-drawer-summary-header/streams-drawer-summary-header';
import '../streams-drawer-summary-view/streams-drawer-summary-view';
import '../streams-drawer-top-bar/streams-drawer-top-bar';
import '../streams-drawer-tracking-bar/streams-drawer-tracking-bar';
import './streams-drawer.less';

class StreamsDrawer {
  public autoConfigTbbEnabled: boolean;
  public contentView: string;
  public launchContext: StreamsLaunchContext;
  public excludedSummaryKeys: string[] = [];
  public isGalleryUploadsStream: boolean;
  public isLegacyShareV2Vendor: boolean;
  public isModerationServices: boolean;
  public isVideoCaptionsFeatureEnabled: boolean;
  // TODO: Use this value below if need be
  public isModerationServicesPublisherClient: boolean;
  public isAssetFeedsDrawer: boolean;
  public selectedStream: any;
  public creatorClients: IShareCreatorClient[];
  public selectStream: (stream) => void;
  public selectedStreamType: StreamsDrawerStreamType;
  public streams: IStream[];
  public recommendationsStreams: IStream[];

  constructor(private SessionVarsService) {}

  public $onInit() {
    this.excludedSummaryKeys = [
      'excludedContentSources',
      'semanticTags',
      'experimentTags',
      'visualScore',
      'minWidth',
      'maxWidth',
      'minHeight',
      'maxHeight',
      'minAspectRatio',
      'maxAspectRatio',
      'excludeMatchedInstagramItemForInstagramStory',
      'excludeMatchedMisItemForInstagramPost',
      'excludeOriginalRepostedInstagramItem',
      'aspectRatio',
    ];

    if (this.isModerationServices) {
      this.launchContext = StreamsLaunchContext.MODERATION_SERVICES;
    } else if (this.isLegacyShareV2Vendor) {
      this.launchContext = StreamsLaunchContext.CONNECT;
    } else if (this.isAssetFeedsDrawer) {
      this.launchContext = StreamsLaunchContext.ASSET_FEEDS;
    } else {
      this.launchContext = StreamsLaunchContext.GALLERIES;
    }
    this.autoConfigTbbEnabled = this.SessionVarsService.isFeatureEnabled(
      'automated_tagged_by_bazaarvoice',
    );
  }

  public $onChanges(changeObj: IOnChangesObject) {
    if (
      changeObj.selectedStreamType &&
      changeObj.selectedStreamType.currentValue
    ) {
      this.isGalleryUploadsStream =
        changeObj.selectedStreamType.currentValue ===
        StreamsDrawerStreamType.GALLERY_UPLOADS;
    }
  }

  public refreshCurrentStream() {
    this.selectStream({ stream: this.selectedStream });
  }

  public shouldShowSidebar() {
    return (
      (!this.isLegacyShareV2Vendor ||
        (this.isModerationServices &&
          ((this.streams && this.streams.length > 1) ||
            (this.recommendationsStreams &&
              this.recommendationsStreams.length > 1) ||
            (this.streams &&
              this.recommendationsStreams &&
              this.streams.length + this.recommendationsStreams.length >
                1)))) &&
      !this.isAssetFeedsDrawer
    );
  }

  public shouldShowStreamName() {
    return (
      !this.isLegacyShareV2Vendor ||
      (this.isModerationServices && this.streams && this.streams.length > 1)
    );
  }

  public calculateStreamsDrawerLayout() {
    return getStreamsDrawerLayout(
      this.selectedStreamType,
      this.contentView,
      this.launchContext,
    );
  }
}

angular.module('curalate').component('streamsDrawer', {
  bindings: {
    assetFeedSourceConnectionStates: '<',
    autoConfigTbbEnabled: '<',
    brandInstagramUsername: '<',
    clientName: '<',
    contentView: '<',
    filterCount: '<',
    filters: '<',
    hasUnsavedAssetFeedFilters: '<',
    hasUserDefinedStream: '<',
    isAssetFeedsOnboarding: '<',
    isAssetFeedStreamUpdatingFilters: '<',
    isContentDiscoveryEnabled: '<',
    isEndOfContentReached: '<',
    isFetchingItems: '<',
    isFetchingFilteredItems: '<',
    isFetchingFirstRunStatus: '<',
    isFetchingNextItems: '<',
    isFiltersSidebarOpen: '<',
    isModerationServices: '<',
    isModerationServicesPublisherClient: '<',
    isRecommendationStream: '<',
    isTaggedByCuralateStream: '<',
    isConnectStream: '<',
    isRefreshingStreams: '<',
    isRemovalConfirmationModalOpen: '<',
    isLegacyShareV2Vendor: '<',
    isStandardStreamModalOpen: '<',
    isDeleteStreamModalOpen: '<',
    isAutoApprovalEntryModalOpen: '<',
    isAutoApprovalModalOpen: '<',
    isAssetFeedsStreamModalOpen: '<',
    isAssetFeedsDrawer: '<?',
    items: '<',
    moderationStatus: '<',
    mentionsMyBrand: '<',
    onAddItems: '&',
    onCompleteAssetFeedsOnboarding: '&',
    onClearSelectedItems: '&',
    onCloseFiltersSidebar: '&',
    onCloseContentDiscoveryFirstRunCard: '&',
    onCloseRemovalConfirmationModal: '&',
    onCloseStreamsDrawer: '&',
    onConfirmAssetFeedsDrawerClose: '&',
    onEndOfContentReached: '&',
    onOpenEditStreamModal: '&',
    onOpenStreamDeleteConfirmationModal: '&',
    onOpenCurrentApprovalModal: '&',
    onOpenAutoApprovalModal: '&',
    onOpenFiltersSidebar: '&',
    onOpenNewStreamModal: '&',
    onOpenRemovalConfirmationModal: '&',
    onRefreshStreams: '&',
    onRemoveItems: '&',
    onSaveAssetFeedsFilters: '&',
    onSetContentView: '&',
    recommendationsStreams: '<',
    selectedItems: '<',
    creatorClients: '<',
    selectedStream: '<',
    selectedStreamType: '<',
    selectStream: '&',
    shouldNotAllowUgcStreams: '<',
    showContentDiscoveryFirstRun: '<',
    selectSummary: '&',
    sort: '<',
    streams: '<',
    summaryItemSets: '<',
    totalItemCount: '<',
    taggedByCuralatePendingCount: '<',
    connectPendingCount: '<',
    trackingTimeout: '<',
    shouldShowGalleryText: '<',
    shouldShowConnectText: '<',
    isVideoActionInProgress: '<',
    isVideoCaptionsFeatureEnabled: '<',
    onClickCaptionTurnOnOff: '&',
  },
  controller: [
    'SessionVarsService',
    'CollectionFiltersConverter',
    StreamsDrawer,
  ],
  template,
});
